<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper';
import LandingTitleLayout from '../LandingTitleLayout.vue';
import { LeftArrowIcon, RightArrowIcon } from '@glow/shared/components/icons';
import { breakpoints } from '@glow/shared/lib/breakpoints';
import { APP_NAME } from '@glow/shared/lib/constants';

const props = withDefaults(
  defineProps<{
    variant?: 'client' | 'partner' | 'help';
  }>(),
  { variant: 'partner' }
);

const { staticUrl } = useRuntimeConfig().public;
</script>

<template>
  <div id="for-whom">
    <LandingTitleLayout>
      <template #title>
        <div v-if="props.variant === 'partner'">
          <span>Для кого</span>
          создан {{ APP_NAME }}?
        </div>
        <div v-else-if="props.variant === 'help'">
          <span>Зачем</span>
          тебе нужен профиль?
        </div>
      </template>
    </LandingTitleLayout>

    <div class="container">
      <Swiper
        :modules="[Navigation, Pagination]"
        :navigation="{
          prevEl: '.slider__prev',
          nextEl: '.slider__next',
        }"
        :breakpoints="{
          [breakpoints['xs']]: {
            slidesPerView: 1,
            spaceBetween: 12,
          },
          [breakpoints['md']]: {
            slidesPerView: 2,
            spaceBetween: 32,
          },
          [breakpoints['lg']]: {
            slidesPerView: 3,
            spaceBetween: 32,
          },
        }"
        pagination
        auto-height
        loop
        class="slider"
      >
        <SwiperSlide v-if="props.variant === 'partner'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/forwhom2.png`"
            class="image"
          />
          <div class="title">Еще учишься</div>
          <div class="text">
            Ты в процессе получения профессии и скоро столкнешься с проблемой
            поиска клиентов
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'partner'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/forwhom1.png`"
            class="image"
          />
          <div class="title">Мастер-новичок</div>
          <div class="text">
            Только закончил(а) обучение и хочешь найти первых клиентов
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'partner'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/forwhom3.png`"
            class="image"
          />
          <div class="title">Работаешь на себя</div>
          <div class="text">
            Ты сам(а) ищешь клиентов на свои услуги и занимаешься своим
            продвижением
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'partner'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/forwhom5.png`"
            class="image"
          />
          <div class="title">Работаешь в салоне</div>
          <div class="text">
            Ты частный мастер, работаешь на салон за % от услуг и хочешь
            расширить клиентскую базу
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'partner'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/forwhom4.png`"
            class="image"
          />
          <div class="title">Хочешь уйти из найма</div>
          <div class="text">
            Ты работаешь в салоне за зарплату и хочешь развиваться автономно и
            работать на себя
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'partner'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/difference6.png`"
            class="image"
          />
          <div class="title">Бьюти – твое хобби</div>
          <div class="text">
            Ты обучаешься и любишь бьюти услуги, но это не твоя основная работа
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'partner'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/difference8.png`"
            class="image"
          />
          <div class="title">Планируешь свое дело</div>
          <div class="text">
            У тебя большая база клиентов, и ты хочешь открыть свой салон и
            увеличить доход
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'help'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/forwhom4.png`"
            class="image"
          />
          <div class="title">Освободись от рутины</div>
          <div class="text">
            Отправь клиентам свой профиль. Они подберут свободне окна для записи
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'help'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/forwhom2.png`"
            class="image"
          />
          <div class="title">Каталог твои работ</div>
          <div class="text">
            Создай личный каталог красоты, чтобы показать свое мастерство
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'help'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/difference6.png`"
            class="image"
          />
          <div class="title">Самостоятельные отзывы</div>
          <div class="text">
            Мы сами предложим твоим клиентам оставить отзыв и поделиться им с
            друзьями
          </div>
        </SwiperSlide>
        <SwiperSlide v-if="props.variant === 'help'" class="slide">
          <NuxtImg
            format="webp"
            :src="`${staticUrl}/landing/why3.png`"
            class="image"
          />
          <div class="title">Все в одном месте</div>
          <div class="text">
            Поделись профилем - все контакты и услуги со стоимостью в один клик
          </div>
        </SwiperSlide>

        <div class="slider__prev"><LeftArrowIcon /></div>
        <div class="slider__next"><RightArrowIcon /></div>
      </Swiper>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.slider {
  height: auto !important;
  padding: 40px 30px;

  @include media-breakpoint-down(sm) {
    padding: 40px 15px;
  }

  &__prev,
  &__next {
    position: absolute;
    top: 50% !important;
    z-index: 9 !important;
    padding: 10px !important;
    color: #fff;
    background: var(--app-gradient-primary) !important;
    border: 3px solid #fff;
    border-radius: 50%;
    cursor: pointer;

    svg {
      width: 28px;
      height: 28px;
    }

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  &__prev {
    left: 0;
  }

  &__next {
    right: 0;
  }
}

.slide {
  .image {
    width: 170px;
    height: 170px;
    margin-bottom: 20px;
  }

  .title {
    font-weight: 600;
    font-size: 24px;
    line-height: 28px;
  }

  .text {
    font-size: 17px;
    line-height: 24px;
    text-align: center;
  }
}
</style>

<style lang="scss">
#for-whom .slide {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  min-height: 420px;
  padding: 32px;
  background: var(--app-color-white-secondary);
  border-radius: 20px;
  cursor: grab;

  @include media-breakpoint-down(xl) {
    min-height: 480px;
  }
}

.swiper-pagination {
  @include media-breakpoint-up(sm) {
    display: none;
  }
}

.swiper-pagination-bullet-active {
  background-color: var(--app-color-primary) !important;
}
</style>
